import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../../config/api';
import { ClipLoader } from 'react-spinners';
import { FaFilter, FaCalendarAlt, FaMapMarkerAlt, FaClock, FaTag, FaExclamationTriangle } from 'react-icons/fa';
import EventImage from '../../../../components/EventImage';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import RequestCard from '../../EventManagement/views/RequestCard.jsx';
import NoEventsMessage from '../../../../components/common/NoEventsMessage.jsx';
import { fixS3ImageUrl } from '../../../../components/imageUtils.js';
import '../css/HostRequest.css';

const logo = '/XPRESS-TICKETS-LOGO2.png';

const HostRequest = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');
    navigate('/');
  };

  const extractLowestPrice = (packages) => {
    if (!packages || !Array.isArray(packages) || packages.length === 0) {
      return 'N/A';
    }

    try {
      const prices = packages
        .map((pkg) => {
          try {
            const parsedPkg = typeof pkg === 'string' ? JSON.parse(pkg) : pkg;
            return parsedPkg.pricing ? parseFloat(parsedPkg.pricing.replace(/[^0-9.]/g, '')) : null;
          } catch (e) {
            console.warn(`Failed to parse package: ${JSON.stringify(pkg)}`, e);
            return null;
          }
        })
        .filter((price) => price !== null && !isNaN(price));

      if (prices.length === 0) {
        return 'N/A';
      }

      const lowestPrice = Math.min(...prices);
      return `R ${lowestPrice.toFixed(2)}`;
    } catch (error) {
      console.error('Price extraction error:', error);
      return 'N/A';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not specified';

    try {
      // Handle ISO strings with timezones as well as simple yyyy-mm-dd
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return 'Date not specified';
      }

      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Date formatting error (HostRequest):', error, 'Date string:', dateString);
      return 'Date not specified';
    }
  };

  const getBackgroundStyle = (rawBackground) => {
    if (!rawBackground) {
      return {};
    }

    let bgArray = rawBackground;

    // Allow background to be either a real array, JSON-stringified array,
    // or a Postgres text[] string like {image,"https://..."}
    if (!Array.isArray(bgArray) && typeof bgArray === 'string') {
      const trimmed = bgArray.trim();

      // First, try JSON.parse (e.g. '["image","https://..."]')
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          bgArray = parsed;
        }
      } catch {
        // If it's not JSON, handle Postgres array syntax
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          const inner = trimmed.slice(1, -1);
          const parts = inner.split(',');
          if (parts.length > 0) {
            bgArray = parts.map((p) => {
              const t = p.trim();
              if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
                return t.slice(1, -1);
              }
              return t;
            });
          }
        }
      }
    }

    if (!Array.isArray(bgArray) || bgArray.length === 0) {
      return {};
    }

    const mode = bgArray[0];
    const value1 = bgArray[1]; // second column in the Background array (URL when mode === 'image')
    const value2 = bgArray[2];

    if (mode === 'solid' && value1) {
      return {
        backgroundColor: value1,
        backgroundImage: 'none',
      };
    }

    if (mode === 'gradient' && value1 && value2) {
      return {
        backgroundImage: `linear-gradient(90deg, ${value1}, ${value2})`,
        backgroundColor: 'transparent',
      };
    }

    if (mode === 'image' && value1) {
      // Process the background image URL through the same fixer/proxy
      // logic used by EventImage so that S3 URLs load correctly.
      const fixedBgUrl = fixS3ImageUrl(value1);
      return {
        backgroundImage: `url(${fixedBgUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'transparent',
      };
    }

    return {};
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      let userId = localStorage.getItem('userId');

      if (!userId) {
        const userData = localStorage.getItem('user');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            userId = user.id;
            if (userId) {
              localStorage.setItem('userId', userId);
            }
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }
      }

      if (!token || !userId) {
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('user');
        navigate('/');
        return;
      }

      let tokenPayload;
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid token format');
        }
        tokenPayload = JSON.parse(atob(tokenParts[1]));
        const now = Math.floor(Date.now() / 1000);
        if (tokenPayload.exp && tokenPayload.exp < now) {
          throw new Error('Token expired');
        }
      } catch (tokenError) {
        console.error('Token validation error:', tokenError);
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('user');
        navigate('/');
        return;
      }

      const apiUrl = `${API_BASE_URL}/organiser/events/pending`;
      console.log('[HostRequest] 📋 Fetching — API: GET /organiser/events/pending');
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('user');
          navigate('/');
          return;
        }
        throw new Error(errorData.message || errorData.error || `Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const eventsData = Array.isArray(data) ? data : [];
      console.log('[HostRequest] ✅ Pending events loaded:', {
        columns: 'id/event_id, event_name/name, location, start_date/event_date, end_date, start_time/event_time, packages[{pricing,...}], background_images_array',
        count: eventsData.length,
      });

      const eventsWithFormattedData = eventsData.map((event) => ({
        // Normalised event shape coming from organiserModel.getPendingEventsByUser
        id: event.id || event.event_id || '',
        eventid: event.id || event.event_id || '',
        event_name: event.event_name || event.name || 'Unnamed Event',
        location: event.location || 'Location not specified',
        date: event.start_date || event.event_date || 'Date not specified',
        endDate: event.end_date || '',
        time: event.start_time || event.event_time || 'Time not specified',
        duration: event.duration ? String(event.duration) : '',
        price: extractLowestPrice(event.packages),
        file_url: event.cover_image_url || event.coverimage || '/default-event-image.jpg',
        description: event.description || 'No description available',
        type: event.event_type || event.type || '',
        capacity: event.total_tickets_planned || event.capacity || 0,
        terms_and_conditions: event.terms_and_conditions || event.terms || '',
        background: event.background_images_array || event.background || null,
        // Extra raw data so ViewEventRequest can prefill the shell
        rawPackages: event.packages || [],
        rawTabs: event.tab_sections || [],
        rawBackgroundArray: event.background_images_array || event.background || null,
        rawEvent: event,
        // Display status for the card (pending host request)
        status: 'pending',
      }));

      // Backend already filters by organiser and Approved = false
      setEvents(eventsWithFormattedData);
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load events. Please try again later.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="hostrequest-container">
        <GlobalHeader
          title="Event Requests"
          subtitle="Manage and view all your event requests"
          backTo="/requestcard"
          logoutTo="/"
          onLogout={handleLogout}
        />
        <div className="hostrequest-loading-container">
          <ClipLoader color="#123abc" loading={loading} size={50} />
          <p className="hostrequest-loading-text">Loading events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isAuthError = error.includes('log in') || error.includes('expired');
    return (
      <div className="hostrequest-container">
        <GlobalHeader
          title="Event Requests"
          subtitle="Manage and view all your event requests"
          backTo={isAuthError ? '/' : '/requestcard'}
          logoutTo="/"
          onLogout={handleLogout}
        />
        <div className="hostrequest-error-container">
          <div className="hostrequest-error-icon">⚠️</div>
          <h3 className="hostrequest-error-title">
            {isAuthError ? 'Authentication Required' : 'Error Loading Events'}
          </h3>
          <p className="hostrequest-error-message">{error}</p>
          <div className="hostrequest-actions">
            {isAuthError ? (
              <button
                className="primary-button"
                onClick={() => {
                  localStorage.clear();
                  navigate('/');
                }}
              >
                Go to Login
              </button>
            ) : (
              <button
                className="primary-button"
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  setEvents([]);
                  fetchEvents();
                }}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hostrequest-container">
      <GlobalHeader
        title="Event Requests"
        subtitle="Manage and view all your event requests"
        backTo="/requestcard"
        logoutTo="/"
        onLogout={handleLogout}
      />

      <main className="hostrequest-main">
        <section className="hostrequest-content">
          {events.length === 0 ? (
            <NoEventsMessage />
          ) : (
            <div className="hostrequest-card-grid">
              {events.map((event) => (
                <article key={event.id} className="hostrequest-card">
                  <div
                    className="hostrequest-card-image-container"
                    style={getBackgroundStyle(event.background)}
                  >
                    <EventImage
                      image={event.file_url}
                      coverimage={event.file_url}
                      alt={event.event_name}
                      className="hostrequest-card-image"
                      loading="eager"
                    />
                  </div>
                  <div className="hostrequest-card-content">
                    <h3 className="hostrequest-card-title">{event.event_name}</h3>
                    <div className="hostrequest-card-details">
                      <div className="hostrequest-detail-item">
                        <FaMapMarkerAlt /> {event.location}
                      </div>
                      <div className="hostrequest-detail-item">
                        <FaCalendarAlt /> {formatDate(event.date)}
                        {event.endDate && ` - ${formatDate(event.endDate)}`}
                      </div>
                      <div className="hostrequest-detail-item">
                        <FaClock /> {event.time}
                        {event.endTime && ` - ${event.endTime}`}
                        {event.duration && ` (${event.duration})`}
                      </div>
                      {event.deadlineDate && (
                        <div className="hostrequest-detail-item hostrequest-deadline">
                          <FaExclamationTriangle />
                          Deadline: {event.deadlineDate} {event.deadlineTime && `at ${event.deadlineTime}`}
                        </div>
                      )}
                      <div className="hostrequest-detail-item">
                        <FaTag /> {event.type || 'Event type not specified'}
                      </div>
                    </div>
                  </div>
                  <div className="hostrequest-card-bottom">
                    <span
                      className={
                        'hostrequest-status ' +
                        (event.status.toLowerCase().includes('approved')
                          ? 'approved'
                          : event.status.toLowerCase().includes('pending')
                          ? 'pending'
                          : 'rejected')
                      }
                    >
                      Status: {event.status}
                    </span>
                    <button
                      type="button"
                      className="view-event-btn"
                      onClick={() => navigate('/ViewEventRequest', { state: { event } })}
                    >
                      View
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default HostRequest;
